import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useDragControls } from "framer-motion";
import { FiX, FiSend, FiPaperclip, FiCheck, FiMic, FiVolume2, FiVolumeX } from "react-icons/fi";
import { RiRobot2Line, RiSparkling2Fill } from "react-icons/ri";
import { chatService, stockService } from "../services/apiService";
import { STORAGE_KEYS } from "../constants/config";
import ChatMarkdown from "./ChatMarkdown";
import useSpeech from "../hooks/useSpeech";

// Starter prompts so the user always knows what they can ask/do.
const QUICK_ACTIONS = [
  "Today's sales",
  "Who owes me money?",
  "Low stock & expiry alerts",
  "This month's profit",
];

// Render an extracted-stock draft as a friendly Markdown table for the chat.
const buildExtractReply = (draft) => {
  const rows = Array.isArray(draft?.rows) ? draft.rows : [];
  const d = draft?.distributor || {};
  const val = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));
  let out = `I found **${rows.length} item(s)** on this bill.\n\n`;
  if (d.billNo || d.name || d.mobileNo) {
    out += `**Distributor:** ${val(d.name)}  |  **Bill No:** ${val(d.billNo)}`;
    if (d.mobileNo) out += `  |  **Mobile:** ${val(d.mobileNo)}`;
    out += "\n\n";
  }
  out += "| Product | Batch | Qty | MRP | Purchase | Expiry |\n";
  out += "| --- | --- | --- | --- | --- | --- |\n";
  rows.slice(0, 30).forEach((r) => {
    out +=
      `| ${val(r.productName)} | ${val(r.batchNo)} | ${val(r.quantity)} | ` +
      `${val(r.mrp)} | ${val(r.purchasePrice)} | ${val(r.expiryDate)} |\n`;
  });
  if (rows.length > 30) out += `\n_…and ${rows.length - 30} more._\n`;
  if (Array.isArray(draft?.notes) && draft.notes.length) {
    out += `\n**Please review:** ${draft.notes.join("; ")}\n`;
  }
  out += "\nReview the items, then open Stock Create to check and save.";
  return out;
};

/**
 * Floating AI chat widget. Reuses the app's JWT (via apiService axios
 * interceptors) and threads the conversation using conversation_id returned
 * by the AI service. Only shown when the user is logged in.
 */
const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text:
        "Hi, I'm **Jash** 👋\n\nI can pull up your sales, stock and payments — " +
        "and even create orders, offers or process returns for you.\n\n" +
        "Try one of the quick actions below, or just ask.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState("");

  const endRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const panelDragControls = useDragControls(); // drag the open panel by its header

  // A bill/sheet the user attached for AI extraction (image / PDF / Excel).
  const [attachedFile, setAttachedFile] = useState(null);
  const MAX_FILE_BYTES = 10 * 1024 * 1024;

  // Voice output (read replies aloud) — opt-in, persisted per user.
  const [voiceOn, setVoiceOn] = useState(() => {
    try { return localStorage.getItem("jashVoiceOn") === "1"; } catch { return false; }
  });
  // Ref so the speech-recognition callback always calls the latest sendMessage.
  const sendMessageRef = useRef(null);

  // Draggable launcher: persist its offset so it stays where the user drops it.
  const dragConstraintsRef = useRef(null);
  const draggedRef = useRef(false); // true if the last pointer gesture was a drag
  const [launcherPos, setLauncherPos] = useState(() => {
    try {
      const saved = localStorage.getItem("jashChatLauncherPos");
      return saved ? JSON.parse(saved) : { x: 0, y: 0 };
    } catch {
      return { x: 0, y: 0 };
    }
  });

  const location = useLocation();
  const navigate = useNavigate();

  const {
    sttSupported,
    ttsSupported,
    listening,
    speaking,
    sttError,
    toggleListening,
    speak,
    cancelSpeak,
  } = useSpeech({
    onFinalTranscript: ({ transcript, isFinal }) => {
      // Show live text as the user speaks; auto-send the completed utterance.
      setInput(transcript);
      if (isFinal && transcript.trim()) {
        sendMessageRef.current?.(transcript);
      }
    },
  });

  // Re-check auth on every route change and on cross-tab storage/focus events,
  // so the widget appears right after login without a full page refresh.
  const [isLoggedIn, setIsLoggedIn] = useState(
    Boolean(localStorage.getItem(STORAGE_KEYS.TOKEN)),
  );

  // Per-client feature flag. Widget stays hidden unless an admin has enabled
  // the ChatBot for this client (chatbotEnabled === true).
  const [chatbotEnabled, setChatbotEnabled] = useState(false);

  useEffect(() => {
    const check = () =>
      setIsLoggedIn(Boolean(localStorage.getItem(STORAGE_KEYS.TOKEN)));
    check();
    window.addEventListener("storage", check);
    window.addEventListener("focus", check);
    return () => {
      window.removeEventListener("storage", check);
      window.removeEventListener("focus", check);
    };
  }, [location]);

  useEffect(() => {
    let cancelled = false;
    if (!isLoggedIn) {
      setChatbotEnabled(false);
      return;
    }
    stockService
      .getChatbotEnabled()
      .then((res) => {
        if (!cancelled) setChatbotEnabled(res?.value === true);
      })
      .catch(() => {
        if (!cancelled) setChatbotEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  useEffect(() => {
    if (open) {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
      inputRef.current?.focus();
    }
  }, [messages, open, loading]);

  // Surface speech-recognition errors in the chat error banner.
  useEffect(() => {
    if (sttError) setError(sttError);
  }, [sttError]);

  // When voice output is on, read the newest assistant reply aloud.
  const lastSpokenRef = useRef(0);
  useEffect(() => {
    if (!voiceOn || !ttsSupported) return;
    const last = messages[messages.length - 1];
    if (last && last.role === "assistant" && messages.length > lastSpokenRef.current) {
      lastSpokenRef.current = messages.length;
      speak(last.text);
    }
  }, [messages, voiceOn, ttsSupported, speak]);

  // Stop any speech when the panel is closed.
  useEffect(() => {
    if (!open) cancelSpeak();
  }, [open, cancelSpeak]);

  const toggleVoice = () => {
    setVoiceOn((prev) => {
      const next = !prev;
      try { localStorage.setItem("jashVoiceOn", next ? "1" : "0"); } catch { /* noop */ }
      if (!next) cancelSpeak();
      return next;
    });
  };

  if (!isLoggedIn || !chatbotEnabled) return null;

  const sendMessage = async (preset, opts = {}) => {
    const { confirmed = false, silent = false } = opts;
    const text = (typeof preset === "string" ? preset : input).trim();
    if (!text || loading) return;

    setError("");
    if (!silent) {
      setInput("");
      setMessages((prev) => [...prev, { role: "user", text }]);
    }
    setLoading(true);

    try {
      const data = await chatService.sendMessage(text, conversationId, confirmed);
      if (data?.conversation_id) setConversationId(data.conversation_id);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: data?.answer || "No response.",
          pendingAction: data?.pending_action || null,
          uiNav: data?.ui_navigation || null,
        },
      ]);
    } catch (e) {
      // 401/403 are handled globally by the axios interceptor (redirect/refresh).
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        "Sorry, I couldn't reach the assistant. Please try again.";
      setError(typeof msg === "string" ? msg : "Request failed.");
    } finally {
      setLoading(false);
    }
  };

  // Keep the latest sendMessage reachable from the speech callback.
  sendMessageRef.current = sendMessage;

  const onPickFile = (e) => {
    const f = e.target.files && e.target.files[0];
    setError("");
    if (f && f.size > MAX_FILE_BYTES) {
      setError("File too large (max 10 MB).");
      setAttachedFile(null);
    } else {
      setAttachedFile(f || null);
    }
    // Allow re-selecting the same file later.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Upload the attached bill/sheet to the extraction endpoint and show the
  // result as a table with a handoff to Stock Create for the reviewed save.
  const extractBill = async () => {
    const file = attachedFile;
    if (!file || loading) return;
    setError("");
    setAttachedFile(null);
    setMessages((prev) => [
      ...prev,
      { role: "user", text: `📎 Uploaded **${file.name}** for stock extraction.` },
    ]);
    setLoading(true);
    try {
      const data = await chatService.extractStock(file);
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      if (rows.length === 0) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            text:
              "I couldn't read any line items from that file. Please try a " +
              "clearer photo or an Excel/PDF with the item list.",
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            text: buildExtractReply(data),
            draft: data, // carried so the action button can hand it off
          },
        ]);
      }
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        "I couldn't analyze that file right now. Please try again.";
      setError(typeof msg === "string" ? msg : "Extraction failed.");
    } finally {
      setLoading(false);
    }
  };

  // Hand the extracted draft to the Stock Create page (via sessionStorage),
  // where the user reviews/edits and saves through the existing flow.
  const openInStockCreate = (draft) => {
    try {
      sessionStorage.setItem("jashStockDraft", JSON.stringify(draft));
    } catch {
      /* ignore quota errors */
    }
    setOpen(false);
    navigate("/stock/create");
  };

  // Open a page the agent proposed (navigate_ui), handing off the prefilled
  // filters via sessionStorage. The target page reads it on mount, fills its
  // existing filter state, and runs its existing search.
  const openUiNav = (nav) => {
    if (!nav || !nav.route) return;
    try {
      sessionStorage.setItem(
        "jashNav",
        JSON.stringify({
          page: nav.page,
          prefill: nav.prefill || {},
          autoSearch: nav.autoSearch !== false,
        }),
      );
    } catch {
      /* ignore quota */
    }
    setOpen(false);
    navigate(nav.route);
  };

  // Confirm a pending write: mark the card resolved, then re-send the last
  // user intent WITH confirmed=true so the agent executes it.
  const confirmAction = (msgIndex) => {
    if (loading) return;
    // Find the most recent user message (the intent that produced this action).
    let intent = "Yes, please proceed.";
    for (let i = msgIndex; i >= 0; i--) {
      if (messages[i].role === "user") {
        intent = messages[i].text;
        break;
      }
    }
    // Resolve the card so its buttons disappear.
    setMessages((prev) =>
      prev.map((m, i) => (i === msgIndex ? { ...m, pendingAction: null, resolved: "confirmed" } : m)),
    );
    sendMessage(intent, { confirmed: true, silent: true });
  };

  const cancelAction = (msgIndex) => {
    setMessages((prev) =>
      prev.map((m, i) =>
        i === msgIndex
          ? { ...m, pendingAction: null, resolved: "cancelled" }
          : m,
      ).concat([{ role: "assistant", text: "Okay, cancelled — nothing was changed." }]),
    );
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (attachedFile) extractBill();
      else sendMessage();
    }
  };

  return (
    <>
      {/* Full-viewport, click-through layer that bounds where the launcher can be dragged */}
      <div ref={dragConstraintsRef} className="pointer-events-none fixed inset-0 z-40" />

      {/* Launcher (draggable) */}
      <motion.div
        drag
        dragConstraints={dragConstraintsRef}
        dragMomentum={false}
        dragElastic={0.05}
        initial={false}
        animate={{ x: launcherPos.x, y: launcherPos.y }}
        onDragStart={() => { draggedRef.current = true; }}
        onDragEnd={(e, info) => {
          const next = {
            x: launcherPos.x + info.offset.x,
            y: launcherPos.y + info.offset.y,
          };
          setLauncherPos(next);
          try { localStorage.setItem("jashChatLauncherPos", JSON.stringify(next)); } catch {}
          // Reset the drag flag shortly after so the click handler can ignore it.
          setTimeout(() => { draggedRef.current = false; }, 0);
        }}
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 cursor-grab active:cursor-grabbing"
      >
        {/* Label pill (hidden when panel is open) */}
        <AnimatePresence>
          {!open && (
            <motion.span
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="hidden select-none items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 shadow-md ring-1 ring-black/5 sm:flex"
            >
              <RiSparkling2Fill className="text-indigo-500" size={12} />
              Ask Jash
            </motion.span>
          )}
        </AnimatePresence>

        {/* Relative wrapper: the button clips its logo background, but the
            status dot lives on this wrapper so it is NOT clipped. */}
        <div className="relative">
          <button
            aria-label={open ? "Close assistant" : "Open Jash assistant"}
            onClick={() => {
              // Ignore the click that ends a drag gesture.
              if (draggedRef.current) return;
              setOpen((v) => !v);
            }}
            className="group relative flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-cover bg-center text-white shadow-lg shadow-blue-500/30 ring-2 ring-white/70 transition-transform duration-200 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-300/60"
            style={{ backgroundImage: "url('/logo.png')" }}
          >
            {/* Dark overlay so the white AI icon on top stays readable */}
            <span className="absolute inset-0 bg-black/40" />

            {/* Pulsing halo when closed */}
            {!open && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-30" />
            )}

            {/* AI icon on top of the logo */}
            <span className="relative z-10 drop-shadow transition-transform duration-200 group-hover:rotate-6">
              {open ? <FiX size={20} /> : <RiRobot2Line size={22} />}
            </span>
          </button>

          {/* Online status dot — sits on the wrapper (not clipped by the button) */}
          {!open && (
            <span className="pointer-events-none absolute right-0 top-0 z-20 flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full border border-white bg-green-500 shadow" />
            </span>
          )}
        </div>
      </motion.div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            drag
            dragControls={panelDragControls}
            dragListener={false}
            dragConstraints={dragConstraintsRef}
            dragMomentum={false}
            dragElastic={0.05}
            className="fixed bottom-24 right-5 z-50 flex h-[70vh] max-h-[560px] w-[92vw] max-w-[380px] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
          >
            {/* Header — drag handle for moving the whole panel */}
            <div
              onPointerDown={(e) => panelDragControls.start(e)}
              className="flex cursor-grab items-center gap-3 bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 px-4 py-3 text-white active:cursor-grabbing">
              <div
                className="relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-cover bg-center ring-2 ring-white/40"
                style={{ backgroundImage: "url('/logo.png')" }}
              >
                <span className="absolute inset-0 bg-black/40" />
                <RiRobot2Line size={16} className="relative z-10 drop-shadow" />
              </div>
              <div className="leading-tight">
                <div className="font-semibold">Jash</div>
                <div className="flex items-center gap-1 text-xs text-blue-100">
                  <span className="inline-block h-2 w-2 rounded-full bg-green-400" />
                  Online
                </div>
              </div>
              {ttsSupported && (
                <button
                  aria-label={voiceOn ? "Turn voice off" : "Turn voice on"}
                  title={voiceOn ? "Voice replies: on" : "Voice replies: off"}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={toggleVoice}
                  className={`relative ml-auto rounded-full p-1.5 transition ${
                    voiceOn ? "bg-white/20 text-white" : "text-white/80 hover:bg-white/15 hover:text-white"
                  }`}
                >
                  {voiceOn ? <FiVolume2 size={16} /> : <FiVolumeX size={16} />}
                  {speaking && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-300 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-green-400" />
                    </span>
                  )}
                </button>
              )}
              <button
                aria-label="Close"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => setOpen(false)}
                className={`${ttsSupported ? "" : "ml-auto"} rounded-full p-1.5 text-white/80 transition hover:bg-white/15 hover:text-white`}
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 space-y-3 overflow-y-auto bg-gray-50 p-3">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${
                    m.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                      m.role === "user"
                        ? "whitespace-pre-wrap rounded-br-sm bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20"
                        : "rounded-bl-sm bg-white text-gray-800 shadow"
                    }`}
                  >
                    {m.role === "user" ? m.text : <ChatMarkdown text={m.text} />}
                    {m.draft && (
                      <button
                        onClick={() => openInStockCreate(m.draft)}
                        className="mt-2 flex items-center gap-1.5 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow hover:from-emerald-600 hover:to-green-700"
                      >
                        <FiSend size={12} /> Open in Stock Create
                      </button>
                    )}
                    {m.uiNav && (
                      <button
                        onClick={() => openUiNav(m.uiNav)}
                        className="mt-2 flex items-center gap-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow hover:from-indigo-600 hover:to-blue-700"
                      >
                        <FiSend size={12} /> Open {m.uiNav.label || "page"}
                      </button>
                    )}
                    {m.pendingAction && (
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          onClick={() => confirmAction(i)}
                          disabled={loading}
                          className="flex items-center gap-1 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow hover:from-emerald-600 hover:to-green-700 disabled:opacity-50"
                        >
                          <FiCheck size={12} /> Confirm
                        </button>
                        <button
                          onClick={() => cancelAction(i)}
                          disabled={loading}
                          className="flex items-center gap-1 rounded-full border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <FiX size={12} /> Cancel
                        </button>
                      </div>
                    )}
                    {m.resolved === "confirmed" && (
                      <div className="mt-1 text-[11px] font-medium text-green-600">
                        <FiCheck size={11} className="inline" /> Confirmed
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="rounded-2xl rounded-bl-sm bg-white px-3 py-2 text-sm text-gray-500 shadow">
                    <span className="inline-flex gap-1">
                      <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.2s]" />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.1s]" />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400" />
                    </span>
                  </div>
                </div>
              )}

              {error && (
                <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
                  {error}
                </div>
              )}

              <div ref={endRef} />
            </div>

            {/* Quick actions — shown early so users know what they can do */}
            {messages.length <= 2 && !loading && (
              <div className="flex flex-wrap gap-1.5 border-t border-gray-100 bg-white px-2 pt-2">
                {QUICK_ACTIONS.map((q) => (
                  <button
                    key={q}
                    onClick={() => sendMessage(q)}
                    className="rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 transition hover:bg-indigo-100"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Attached-file chip */}
            {attachedFile && (
              <div className="flex items-center gap-2 border-t border-gray-100 bg-indigo-50/60 px-3 py-1.5 text-xs text-indigo-800">
                <FiPaperclip size={12} />
                <span className="flex-1 truncate">{attachedFile.name}</span>
                <button
                  onClick={extractBill}
                  disabled={loading}
                  className="rounded-full bg-indigo-600 px-2.5 py-0.5 font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  Extract
                </button>
                <button
                  onClick={() => setAttachedFile(null)}
                  className="text-indigo-400 hover:text-red-600"
                  aria-label="Remove file"
                >
                  <FiX size={14} />
                </button>
              </div>
            )}

            {/* Input */}
            <div className="flex items-end gap-2 border-t border-gray-200 bg-white p-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf,.xlsx,.xls,.csv,.txt"
                onChange={onPickFile}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                aria-label="Attach a bill or stock sheet"
                title="Attach a bill / stock sheet for AI extraction"
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-gray-300 text-gray-500 transition hover:bg-gray-50 hover:text-indigo-600 disabled:opacity-50"
              >
                <FiPaperclip size={18} />
              </button>
              {sttSupported && (
                <button
                  onClick={toggleListening}
                  disabled={loading}
                  aria-label={listening ? "Stop listening" : "Speak your message"}
                  title={listening ? "Listening… tap to stop" : "Tap to speak"}
                  className={`relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border transition disabled:opacity-50 ${
                    listening
                      ? "border-red-300 bg-red-50 text-red-600"
                      : "border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-indigo-600"
                  }`}
                >
                  <FiMic size={18} />
                  {listening && (
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-xl bg-red-400 opacity-30" />
                  )}
                </button>
              )}
              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={
                  listening
                    ? "Listening… speak now"
                    : attachedFile
                      ? "Press Extract to read the file…"
                      : "Type or tap the mic to speak…"
                }
                maxLength={4000}
                className="max-h-28 flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
              <button
                onClick={() => (attachedFile ? extractBill() : sendMessage())}
                disabled={loading || (!input.trim() && !attachedFile)}
                aria-label="Send"
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 text-white shadow-md shadow-blue-500/30 transition hover:from-indigo-600 hover:to-blue-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
              >
                <FiSend size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
